import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, inr, TYPE_LABELS } from "@/lib/api";
import { CandleChart, BAND_STYLE, VOL_STYLE } from "@/components/CandleChart";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { WatchAdd } from "@/components/WatchAdd";
import { TfrLevels } from "@/components/TfrLevels";
import { ArrowLeft, BookmarkPlus, Loader2 } from "lucide-react";

const TFS = [["5m", "5m"], ["15m", "15m"], ["60m", "1h"]];

export default function InstrumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [instrument, setInstrument] = useState(null);
  const [tf, setTf] = useState("5m");
  const [chart, setChart] = useState(null);
  const [live, setLive] = useState(null);
  const [lv, setLv] = useState(null);

  useEffect(() => {
    api.get(`/instruments/${id}`).then(({ data }) => setInstrument(data.instrument));
    api.get(`/instruments/${id}/tfr-levels`).then((r) => setLv(r.data)).catch(() => setLv({ for_date: null }));
  }, [id]);
  const levels = useMemo(() => !lv?.for_date ? [] : [
    { price: lv.d, color: "#FF6D00", width: 2, title: "TFR D" },
    { price: lv.w, color: "#FF4081", width: 2, title: "TFR W" },
    { price: lv.adr_hi, color: "#38bdf8", style: 2, title: "RM H" },
    { price: lv.adr_lo, color: "#38bdf8", style: 2, title: "RM L" },
  ], [lv]);

  useEffect(() => {
    setChart(null);
    api.get(`/instruments/${id}/bands`, { params: { timeframe: tf } })
      .then(({ data }) => setChart(data)).catch(() => setChart({ candles: [], bands: {} }));
  }, [id, tf]);

  // live candles: poll the in-memory Dhan tick every 2s while the market is open; refresh completed bars every minute
  useEffect(() => {
    let alive = true;
    const tick = () => api.get(`/instruments/${id}/live`).then(({ data }) => alive && data.market_open && data.ltp && setLive(data)).catch(() => {});
    const bars = () => api.get(`/instruments/${id}/bands`, { params: { timeframe: tf } }).then(({ data }) => alive && setChart(data)).catch(() => {});
    tick();
    const t1 = setInterval(tick, 2000);
    const t2 = setInterval(bars, 60000);
    return () => { alive = false; clearInterval(t1); clearInterval(t2); };
  }, [id, tf]);

  const addWatch = async () => {
    const { data } = await api.get("/watchlists");
    await api.post(`/watchlists/${data.watchlists[0].id}/items`, { instrument_id: id });
    toast.success("Added to watchlist");
  };

  if (!instrument) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  const last = live?.ltp ? { c: live.ltp } : chart?.candles?.[chart.candles.length - 1];

  return (
    <div>
      <Button variant="ghost" className="text-slate-500 mb-3 px-2" onClick={() => navigate(-1)} data-testid="detail-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2" data-testid="detail-symbol">{instrument.display_symbol}
              <WatchAdd instrumentId={instrument.id} symbol={instrument.symbol} className="h-7 w-7" /></h1>
            <Badge variant="secondary" className="bg-slate-100 text-slate-500">{TYPE_LABELS[instrument.type]}</Badge>
            {last && <span className="font-mono text-lg text-slate-900" data-testid="detail-ltp">₹{inr(last.c)}</span>}
            {live?.ltp && <span className="flex items-center gap-1 text-[10px] font-mono text-emerald-700" data-testid="detail-live"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE</span>}
          </div>
          <p className="text-sm text-slate-500 mt-1">{instrument.name} · {instrument.sector}</p>
        </div>
        <Button variant="outline" className="border-slate-300" onClick={addWatch} data-testid="detail-watch-button"><BookmarkPlus className="h-4 w-4 mr-1" /> Watchlist</Button>
      </div>

      <TfrLevels lv={lv} ltp={live?.ltp || last?.c} />

      <Card className="bg-white border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex flex-wrap gap-4 text-[11px] font-mono text-slate-600" data-testid="chart-legend">
            {Object.entries(BAND_STYLE).map(([k, s]) => (
              <span key={k} className="flex items-center gap-1.5"><span className="w-4 h-2.5 rounded-sm inline-block border" style={{ background: s.fill, borderColor: s.color }} /> {s.label}</span>
            ))}
            {Object.entries(VOL_STYLE).map(([k, s]) => (
              <span key={k} className="flex items-center gap-1.5" data-testid={`legend-vol-${k}`}><span className="w-2 h-3 rounded-sm inline-block" style={{ background: s.color }} /> {s.label}</span>
            ))}
          </div>
          <div className="flex rounded-md border border-slate-300 overflow-hidden">
            {TFS.map(([t, lbl]) => (
              <button key={t} onClick={() => setTf(t)} data-testid={`detail-tf-${t}`}
                className={`px-3 py-1 text-xs font-mono ${tf === t ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{lbl}</button>
            ))}
          </div>
        </div>
        {chart ? <CandleChart candles={chart.candles} bands={chart.bands} height={480} live={live} levels={levels} tfMinutes={{ "5m": 5, "15m": 15, "60m": 60 }[tf]} />
          : <div className="h-[480px] grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>}
      </Card>
    </div>
  );
}
